/**
 * What a post-sale covenant PRODUCES when the work sells again inside its term.
 *
 * A covenant fixes a share of the PROFIT at the first sale; a settlement is that
 * share applied to the second. It comes into existence only on a resale, and it
 * is written by `ondo.write_resale_settlements()` at the moment that resale
 * stands — see `20260906090000_resale_settlements.sql`, decision 7.
 *
 * NOTHING HERE COMPUTES MONEY. The arithmetic lives in
 * `ondo.settlement_arithmetic()` and its result is frozen onto the row, for the
 * reason the migration gives: a figure recomputed on read is a figure that can
 * change under a party who already acted on it. The functions below DERIVE
 * STATE from figures the database already decided — which is the same division
 * `covenantState` keeps in `transfer.ts`.
 *
 * There is NO Postgres enum behind these: the states are derived from
 * timestamps, so they must stay in step with the columns of
 * `ondo.resale_settlements` rather than with a type. Nothing enforces that but
 * this comment, which is the arrangement every other vocabulary here has.
 */
/**
 * What a settlement is doing right now.
 *
 * Derived, never stored — there is no `status` column, and adding one would be
 * a second source of truth for something six timestamps already answer.
 *
 * 'awaiting_rate'  the resale was in another currency and nobody has stated one
 * 'due'            a share is owed and nothing has been collected
 * 'collected'      the reseller has paid; the artist has not been paid on
 * 'settled'        both legs recorded
 * 'nothing_owed'   the resale made no profit, or the artist did not elect
 * 'void'           the resale was withdrawn
 */
export const SETTLEMENT_STATES = [
    "awaiting_rate",
    "due",
    "collected",
    "settled",
    "nothing_owed",
    "void",
];
/**
 * The order matters, and it is the order of finality.
 *
 * VOID FIRST: a withdrawn resale ends the obligation whatever else was recorded
 * against it, including a collection. A gallery that collected and then saw the
 * sale unsaid has a real problem, and a row reading 'collected' would be the
 * screen telling them they have none.
 *
 * AWAITING BEFORE NOTHING_OWED: a settlement with no figures is not a
 * settlement that owes nothing. Reading `shareMinor === 0` off a null would
 * announce that a resale made no profit before anyone has said what it made.
 */
export function settlementState(row) {
    if (row.voidedAt !== null)
        return "void";
    if (row.shareMinor === null)
        return "awaiting_rate";
    if (row.shareMinor === 0)
        return "nothing_owed";
    if (row.remittedAt !== null)
        return "settled";
    return row.collectedAt !== null ? "collected" : "due";
}
/**
 * Which of the screen's groups a settlement belongs to.
 *
 * Three groups, not six states, because a gallery reading this screen is doing
 * one of three things — chasing what is owed, checking what is closed, or
 * looking back at what a covenant did — and 'collected' is not a fourth: a
 * settlement whose artist has not been paid is still open work.
 */
export const SETTLEMENT_GROUPS = ["due", "closed", "nothing_owed"];
export function settlementGroup(state) {
    switch (state) {
        case "awaiting_rate":
        case "due":
        case "collected":
            return "due";
        case "settled":
            return "closed";
        // A VOID settlement sits with the ones that owe nothing rather than with
        // the settled ones. It was not settled — it stopped being owed, and filing
        // it under "closed" would let a gallery read a withdrawn sale as a paid one.
        case "void":
        case "nothing_owed":
            return "nothing_owed";
    }
}
/**
 * Whether the gallery's next act on this row is to state a rate.
 *
 * Separate from the state so a view never re-derives it from a string: the
 * control this gates takes a number, and every other control on the screen
 * does not.
 */
export function needsRate(row) {
    return (row.voidedAt === null &&
        row.shareMinor === null &&
        row.resaleCurrency !== row.currency);
}
/**
 * Whether the artist's half was never owed, as opposed to owed and zero.
 *
 * `artistElected` false means the artist's half "is not owed at all, and a
 * settlement records zero rather than skipping the row" —
 * `20260903090000_transfers.sql`. A row showing a 40% split beside £0 with no
 * explanation reads as a bug in the arithmetic; this is the explanation.
 */
export function artistWasNotElected(row) {
    return !row.artistElected && row.artistSplitBp > 0;
}
/** Basis points as a percentage string: 4000 → '40%', 1250 → '12.5%'. */
export function percentOfBp(bp) {
    // Trailing zeros dropped, so a whole percentage does not read as a precision
    // the term does not have. 1000 is '10%', not '10.0%'.
    return `${Number((bp / 100).toFixed(2))}%`;
}
//# sourceMappingURL=settlement.js.map